import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { of, Subscription, timer } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { AuthActions } from './auth.actions';
import { ToastService } from '../../../../../projects/client/src/app/shared/services';
import { AuthorisationTokenService } from '../../../website/services/authorisation-token.service';
import { AUTHORISED_CHATBOT_USERS, RENEW_FRACTION } from '../auth.config';
import { ChatbotActions } from '../../../website/shared/chatbot/state/chatbot.actions';
import { User } from '../../../website/models';

export interface AuthStateModel {
  isAuthenticated: boolean;
  accessToken: string | null;
  tokenType: string | null;
  validUntil: number | null; // ms
  user: User | null;
}

@State<AuthStateModel>({
  name: 'auth',
  defaults: {
    isAuthenticated: false,
    accessToken: null,
    tokenType: null,
    validUntil: null,
    user: null
  }
})
@Injectable()
export class AuthState {
  private inflight = false;
  private retryAttempts = 0;
  private readonly maxRetries = 3;
  private refreshSub?: Subscription;

  constructor(
    private readonly tokenSvc: AuthorisationTokenService,
    private readonly toast: ToastService
  ) {}

  // Selectors
  @Selector()
  static header(s: AuthStateModel) {
    return s.accessToken && s.tokenType ? `${s.tokenType} ${s.accessToken}` : null;
  }

  @Selector()
  static isTokenValid(s: AuthStateModel) {
    const exp = s.validUntil ?? 0;
    const token = s.accessToken;
    return !!token && Date.now() + 15_000 < exp; // 15s early cutoff
  }

  @Selector()
  static token(s: AuthStateModel) {
    return { accessToken: s.accessToken, tokenType: s.tokenType, validUntil: s.validUntil };
  }

  @Selector()
  static canSeeChat(s: AuthStateModel) {
    return s.user?.permissions.includes(AUTHORISED_CHATBOT_USERS);
  }

  // actions
  @Action(AuthActions.EnsureToken, { cancelUncompleted: true })
  ensureToken(ctx: StateContext<AuthStateModel>, { force }: AuthActions.EnsureToken) {
    const valid = AuthState.isTokenValid(ctx.getState());
    if (valid && !force) return of(true);
    if (this.inflight) return of(false);

    this.inflight = true;
    return this.tokenSvc.generateJWTAccessToken().pipe(
      map((res) => {
        const expiresAtMs = Date.now() + res.expiresIn * 1000;
        ctx.dispatch(new AuthActions.TokenSuccess(res.accessToken, res.tokenType, expiresAtMs));
        return true;
      }),
      catchError((error) => {
        ctx.dispatch(new AuthActions.TokenFailure(error));
        return of(false);
      }),
      finalize(() => {
        this.inflight = false;
      })
    );
  }

  @Action(AuthActions.TokenSuccess)
  onSuccess(ctx: StateContext<AuthStateModel>, { accessToken, tokenType, validUntil }: AuthActions.TokenSuccess) {
    // validUntil already ms epoch — no extra conversion needed
    ctx.patchState({ isAuthenticated: true, accessToken, tokenType, validUntil });
    this.retryAttempts = 0;

    this.refreshSub?.unsubscribe();
    // schedule refresh at 80% of remaining lifetime
    const lifetimeMs = Math.max(0, validUntil - Date.now());
    const renewInMs = Math.floor(lifetimeMs * RENEW_FRACTION);
    this.refreshSub = timer(renewInMs).subscribe(() => {
      ctx.dispatch(new AuthActions.EnsureToken(true));
    });
  }

  @Action(AuthActions.TokenFailure)
  onFailure(ctx: StateContext<AuthStateModel>, { error }: AuthActions.TokenFailure) {
    const status = error?.status;

    // 401 → clear, notify, stop scheduling
    if (status === 401) {
      this.toast.add({ header: 'Token request failed.', message: 'Please log in again.' }, 'error');
      return ctx.dispatch(new AuthActions.ClearToken());
    }

    // 4xx no retry
    if (status >= 400 && status < 500) {
      this.toast.add(
        {
          header: `Auth token request failed.`,
          message: `${status}`
        },
        'error'
      );
      return;
    }

    // retry network/5xx with capped backoff
    if (status === 0 || (status >= 500 && status < 600)) {
      if (this.retryAttempts < this.maxRetries) {
        this.retryAttempts++;
        const delay = 1000 * Math.pow(2, this.retryAttempts - 1);
        this.toast.add({ header: `Retrying auth token.`, message: `(attempt ${this.retryAttempts})` }, 'info');
        return timer(delay).pipe(tap(() => ctx.dispatch(new AuthActions.EnsureToken())));
      }
      this.toast.add({ header: 'Failed to get access token after multiple attempts.' }, 'error');
      this.retryAttempts = 0;
    }
  }

  @Action(AuthActions.ClearToken)
  clear(ctx: StateContext<AuthStateModel>) {
    this.refreshSub?.unsubscribe();
    this.refreshSub = undefined;
    ctx.setState({
      isAuthenticated: false,
      accessToken: null,
      tokenType: null,
      validUntil: null,
      user: null
    });
  }

  @Action(AuthActions.LoginSuccess)
  loginSuccess(ctx: StateContext<AuthStateModel>, { user }: AuthActions.LoginSuccess) {
    ctx.patchState({
      user,
      isAuthenticated: true
    });

    // decide visibility without calling API if not admin
    const canSeeChat = AuthState.canSeeChat(ctx.getState());
    if (canSeeChat) {
      ctx.dispatch(new ChatbotActions.LoadLatest());
    } else {
      ctx.dispatch([
        new ChatbotActions.HasErrorLoadingConversation(null),
        new ChatbotActions.Reset() // clears messages/convo
      ]);
    }
  }

  @Action(AuthActions.SetUser)
  setUser(ctx: StateContext<AuthStateModel>, { user }: AuthActions.SetUser) {
    ctx.patchState({
      user,
      isAuthenticated: !!user && AuthState.isTokenValid(ctx.getState())
    });

    if (user) {
      return ctx.dispatch(new AuthActions.EnsureToken()).pipe(
        switchMap(() => {
          ctx.dispatch(new ChatbotActions.Reset());
          return ctx.dispatch(new ChatbotActions.LoadLatest());
        })
      );
    } else {
      // no user: ensure chatbot is fully reset/hidden
      return ctx.dispatch(new ChatbotActions.Reset());
    }
  }

  @Action([AuthActions.LogoutSuccess])
  onAuthIdentityChange(ctx: StateContext<AuthStateModel>) {
    ctx.dispatch(new ChatbotActions.Reset());
  }
}
